import CalendarReportMenu from './Calendar/components/CalendarReportMenu';
import React, { useState, useEffect, useContext, useRef, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { UserContext } from '../contexts/UserContext';
import { useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxon3Plugin from '@fullcalendar/luxon3';
import '@fullcalendar/common/main.css';
import { io } from 'socket.io-client';
import { getSocketOrigin } from '../../utils/runtimeUrls';
import { DateTime } from 'luxon';
import LimitSettingsModal from './LimitSettingsModal';
import EventModal from './modals/EventModal';
import { apiCallJson } from '../utils/auth';
import { canUseCalendarAdminMode, hasAdminAccess } from '../utils/permissionConfig';
import { api as apiClient } from '../services/api';
import './Calendar.css';
import { MODERN_THEME } from '../styles/themes';
import { CALENDAR_DISPLAY } from '../constants/calendarDisplay';

// 분리된 유틸리티 및 locale 설정
import {
  DEFAULT_TIMEZONE,
  toParisISOString,
  toUTCISOStringFromLocalInput,
  toCalendarEvent,
  isOverlappingLimit,
  hasRestrictedPeriodOverlap,
  isCalendarEventCreationMode,
  isFullDayRestrictedPeriodEvent,
  isFullCalendarEventElementClick,
  findBlockingRestrictedPeriods,
  formatStudioUnavailableMessage,
  toRestrictedPeriodCalendarEvent
} from './Calendar/utils/calendarHelpers';
import { eventBlockAPI, loadMaximumConcurrentLimit, loadPublicEventBlocks } from '../utils/limitSettings';
import {
  customLocale,
  customKoLocale,
  customFrLocale,
  customEnLocale,
  getCalendarLocale
} from './Calendar/utils/localeConfig';
import { createCalendarPerformanceTracker } from './Calendar/utils/performanceTracker';
import {
  canEditCalendarEvent,
  getCalendarEventMutationPath,
  getCalendarEventMutationScope,
  shouldPassCalendarEventClickThrough
} from '../utils/calendarEventPermissions';
import {
  CalendarDayOffEventContent,
  CalendarMonthEventCircle,
  CalendarTimeGridEventContent
} from './Calendar/components/EventContent';

// React Query 훅
import { useDayOffsQuery } from '../lib/useApiQuery';

// 개발 환경 체크 헬퍼
const isDev = () => process.env.NODE_ENV === 'development';

const AddEventModal = lazy(() => import('./AddEventModal'));
const EditEventModal = lazy(() => import('./EditEventModal'));
const MODAL_LOADING_TEXT = {
  ko: '모달 불러오는 중...',
  en: 'Loading modal...',
  fr: 'Chargement...'
};

// 다국어 지원을 위한 TEXT 상수
const TEXT = {
  today: { ko: '오늘', en: 'Today', fr: 'Auj.' },
  month: { ko: '월', en: 'M.', fr: 'M.' },
  week: { ko: '주', en: 'W.', fr: 'S.' },

  day: { ko: '일', en: 'D.', fr: 'J.' },

  // 0-8시 시간대 토글 버튼 텍스트
  expandEarlyHours: { ko: '0-8시 펼치기', en: 'Expand 0-8h', fr: 'Déplier 0-8h' },
  collapseEarlyHours: { ko: '0-8시 접기', en: 'Collapse 0-8h', fr: 'Replier 0-8h' },

  normalMode: { ko: '일반', en: 'NORMAL', fr: 'NORMAL' },
  addMode: { ko: '추가', en: 'ADD', fr: 'AJOUT' },
  offdayMode: { ko: '휴무', en: 'OFF', fr: 'CONGÉ' },
  adminMode: { ko: '관리', en: 'ADMIN', fr: 'ADMIN' },
  bidonMode: { ko: 'BIDON', en: 'BIDON', fr: 'BIDON' },
  limitSettings: { ko: '제한설정', en: 'Limits', fr: 'Limites' },
  eventUpdateError: { ko: '이벤트 수정 중 오류가 발생했습니다.', en: 'Error updating event.', fr: 'Erreur lors de la modification de l\'événement.' },
  startEndTimeRequired: { ko: '시작 시간과 종료 시간이 필요합니다.', en: 'Start and end times are required.', fr: 'Les heures de début et de fin sont requises.' },
  endTimeAfterStart: { ko: '종료 시간은 시작 시간보다 늦어야 합니다.', en: 'End time must be after start time.', fr: 'L\'heure de fin doit être après l\'heure de début.' },
  updatingEvent: { ko: '수정 중...', en: 'Updating...', fr: 'Modification...' },
  helpTitle: { ko: '모드 설명', en: 'Mode Description', fr: 'Description du mode' },
  helpNormal: { ko: '일반(NORMAL): 예약을 확인하고 관리하는 기본 모드입니다.', en: 'NORMAL: Basic mode to check and manage reservations.', fr: 'NORMAL: Mode de base pour vérifier et gérer les réservations.' },
  helpAdd: { ko: '추가(ADD): 새로운 예약을 생성하는 모드입니다.', en: 'ADD: Mode to create new reservations.', fr: 'AJOUT: Mode pour créer de nouvelles réservations.' },
  helpOff: { ko: '휴무(OFF): 휴무일을 설정하는 모드입니다.', en: 'OFF: Mode to set days off.', fr: 'CONGÉ: Mode pour définir les jours de congé.' },
  helpAdmin: { ko: '관리(ADMIN): 빈 구간을 드래그해 금지기간을 만들고, 기존 금지 박스를 클릭해 수정하거나 삭제합니다.', en: 'ADMIN: Drag an empty range to block it, or click an existing blocked range to edit or delete it.', fr: 'ADMIN : Faites glisser une plage vide pour la bloquer, ou cliquez sur une plage bloquée pour la modifier ou la supprimer.' },
  eventUpdatedSuccess: { ko: '이벤트가 성공적으로 수정되었습니다.', en: 'Event updated successfully.', fr: 'Événement modifié avec succès.' },
  noPermission: { ko: '내 일정이 아니라서 권한이 없습니다.', en: 'No permission - not your event.', fr: 'Pas de permission - pas votre événement.' },
  overlappingLimit: { ko: '이미 {limit}개의 이벤트가 있습니다. 최대 {limit}개까지 허용됩니다.', en: 'There are already {limit} events. Maximum {limit} allowed.', fr: 'Il y a déjà {limit} événements. Maximum {limit} autorisés.' },
  blockedDay: { ko: '일정추가 금지일입니다.', en: 'Event addition blocked day.', fr: 'Jour bloqué pour l\'ajout d\'événements.' },
  studioUnavailable: { ko: '이 날은 스튜디오 이용이 불가능합니다.', en: 'Studio unavailable on this day.', fr: 'Studio indisponible ce jour.' },
  userInfoError: { ko: '사용자 정보를 불러올 수 없습니다. 페이지를 새로고침해주세요.', en: 'Cannot load user info. Please refresh the page.', fr: 'Impossible de charger les infos utilisateur. Veuillez rafraîchir la page.' },
  dayOffDeleteError: { ko: 'DayOff 삭제에 실패했습니다.', en: 'Failed to delete DayOff.', fr: 'Échec de la suppression du congé.' },
  dayOffAddError: { ko: 'DayOff 등록에 실패했습니다.', en: 'Failed to add DayOff.', fr: 'Échec de l\'ajout du congé.' },
  blockDeleteError: { ko: '금지기간 삭제에 실패했습니다.', en: 'Failed to delete blocked period.', fr: 'Échec de la suppression de la période bloquée.' },
  blockAddError: { ko: '금지기간 등록에 실패했습니다.', en: 'Failed to add blocked period.', fr: 'Échec de l\'ajout de la période bloquée.' },
  blockRemoved: { ko: '금지기간이 해제되었습니다.', en: 'Blocked period removed.', fr: 'Période bloquée supprimée.' },
  blockAdded: { ko: '금지기간이 설정되었습니다.', en: 'Blocked period added.', fr: 'Période bloquée ajoutée.' },
  blockUpdated: { ko: '금지기간이 수정되었습니다.', en: 'Blocked period updated.', fr: 'Période bloquée modifiée.' },
  blockUpdateError: { ko: '금지기간 수정에 실패했습니다.', en: 'Failed to update blocked period.', fr: 'Échec de la modification de la période bloquée.' },
  blockOverlap: { ko: '선택한 구간에 이미 금지기간이 있습니다.', en: 'A blocked period already exists in this range.', fr: 'Une période bloquée existe déjà sur ce créneau.' },
  restrictedPeriodLabel: { ko: '금지', en: 'Blocked', fr: 'Bloqué' },
  unknownError: { ko: '알 수 없는 오류가 발생했습니다.', en: 'An unknown error occurred.', fr: 'Une erreur inconnue s\'est produite.' },
  concurrentLimitExceeded: { ko: '동시일정 제한 초과: 기본 제한 ({limit}개) - 전역 기본 설정', en: 'Concurrent event limit exceeded: Default limit ({limit}) - Global default setting', fr: 'Limite d\'événements simultanés dépassée: Limite par défaut ({limit}) - Paramètre global par défaut' },
  concurrentLimitExceededDefault: { ko: '동시일정 제한 초과: 기본 제한 ({limit}개) - 전역 기본 설정', en: 'Concurrent event limit exceeded: Default limit ({limit}) - Global default setting', fr: 'Limite d\'événements simultanés dépassée: Limite par défaut ({limit}) - Paramètre global par défaut' },
};

// DEFAULT_TIMEZONE은 calendarHelpers.js에서 import됨

// 다국어 텍스트 변환 함수 (Calendar 내부용 - TEXT 객체 사용)
const t = (key, lang, params = {}) => {
  const text = TEXT[key]?.[lang] || TEXT[key]?.['ko'] || key;
  if (params.limit) {
    return text.replace(/{limit}/g, params.limit);
  }
  return text;
};

const isTimeGridView = (viewType) => (
  viewType === 'timeGridWeek' ||
  viewType === 'timeGridDay' ||
  viewType === 'timeGridWeek30'
);

const DEFAULT_CLICK_EVENT_DURATION_MS = 30 * 60 * 1000;
const STUDIO_UNAVAILABLE_ALERT_COOLDOWN_MS = 750;

const toRestrictedPeriodInputValue = (value, isAllDay) => {
  const dateTime = value instanceof Date
    ? DateTime.fromJSDate(value).setZone(DEFAULT_TIMEZONE)
    : DateTime.fromISO(String(value), { setZone: true }).setZone(DEFAULT_TIMEZONE);

  if (!dateTime.isValid) {
    return '';
  }

  return dateTime.toFormat(isAllDay ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm");
};

const parseSlotTimeToMinutes = (timeValue) => {
  if (!timeValue) {
    return null;
  }

  const [hourText = '0', minuteText = '0'] = timeValue.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }

  return (hour * 60) + minute;
};

const CalendarModalFallback = ({ lang }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(12, 12, 12, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000001,
      pointerEvents: 'none',
    }}
  >
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.94)',
        color: '#111',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: '14px',
        padding: '14px 18px',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      {MODAL_LOADING_TEXT[lang] || MODAL_LOADING_TEXT.ko}
    </div>
  </div>
);

// toParisISOString, toUTCISOStringFromLocalInput은 calendarHelpers.js에서 import됨

// toCalendarEvent 함수는 calendarHelpers.js에서 import됨

// isOverlappingLimit 함수는 calendarHelpers.js에서 import됨

// getCalendarLocale, customLocale, customKoLocale, customFrLocale, customEnLocale은 
// localeConfig.js에서 import됨

export default function Calendar() {
  const [safetyEvents,setSafetyEvents] = useState([]);
  useEffect(()=>{const refresh=()=>{setSafetyEvents([]);calendarRef.current?.getApi()?.refetchEvents();};window.addEventListener('safety-changed',refresh);return()=>window.removeEventListener('safety-changed',refresh);},[]);
  // events/setEvents 미사용 - React Query 사용 중
  // const [events, setEvents] = useState([]);
  const { userInfo } = useContext(UserContext);
  const location = useLocation();

  // 언어 설정
  const lang = userInfo?.preferred_language || 'ko';
  const hasCalendarAdminModeAccess = canUseCalendarAdminMode(userInfo);
  const hasFullAdminAccess = hasAdminAccess(userInfo);

  // React Query: 휴무일 데이터 조회 (기존 refetchDayOff 대체)
  const { data: rawDayOffData, refetch: refetchDayOffQuery } = useDayOffsQuery(userInfo?.user_id);

  const calendarRef = useRef();
  const calendarWrapperRef = useRef();
  const adjustTitleSizeTimeoutRef = useRef(null);
  const currentTimeLineTimeoutRef = useRef(null);
  const refetchTimeoutRef = useRef(null);
  const timeGridDecorationsTimeoutRef = useRef(null);
  const timeGridStructureTimeoutRef = useRef(null);
  const fetchEventsRetryCountRef = useRef(0);
  const fetchEventsRequestIdRef = useRef(0);
  const stickyHeaderRef = useRef(null);
  const stickyHeaderSyncTimeoutRef = useRef(null);
  const stickyHeaderRafRef = useRef(null);
  const calendarPerfRef = useRef(null);

  if (!calendarPerfRef.current) {
    calendarPerfRef.current = createCalendarPerformanceTracker('mainCalendar');
  }

  const calendarPerf = calendarPerfRef.current;

  useEffect(() => {
    if (!calendarPerf.enabled) {
      return undefined;
    }

    calendarPerf.count('lifecycle.mount');
    console.info('[CALENDAR PERF] window.__calendarPerf.mainCalendar.snapshot() / logSummary() / reset()');

    return () => {
      calendarPerf.count('lifecycle.unmount');
    };
  }, [calendarPerf]);

  useEffect(() => {
    if (calendarPerf.enabled) {
      calendarPerf.count('render.commits');
    }
  });

  const getCalendarApi = useCallback(() => {
    return calendarRef.current?.getApi() || null;
  }, []);

  const getCalendarElement = useCallback(() => {
    return calendarWrapperRef.current?.querySelector('.fc') || null;
  }, []);

  const getCalendarHeaderElement = useCallback(() => {
    return getCalendarElement()?.querySelector('.fc-col-header') || null;
  }, [getCalendarElement]);

  const clearScheduledCalendarRefresh = useCallback(() => {
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = null;
    }
  }, []);

  const requestCalendarRefresh = useCallback((delay = 200, reason = 'unknown') => {
    if (refetchTimeoutRef.current) {
      calendarPerf.count('refetch.skipped.pending', 1, { reason });
      return;
    }

    calendarPerf.count('refetch.scheduled', 1, { delay, reason });
    calendarPerf.sample('refetch.scheduledDelayMs', delay, { reason });

    refetchTimeoutRef.current = setTimeout(() => {
      refetchTimeoutRef.current = null;

      const calendarApi = getCalendarApi();
      if (calendarApi) {
        calendarPerf.count('refetch.executed', 1, { reason });
        calendarApi.refetchEvents();
      } else {
        calendarPerf.count('refetch.skipped.noApi', 1, { reason });
      }
    }, delay);
  }, [calendarPerf, getCalendarApi]);

  useEffect(() => {
    return () => {
      clearScheduledCalendarRefresh();
    };
  }, [clearScheduledCalendarRefresh]);

  const clearScheduledCurrentTimeLineUpdate = useCallback(() => {
    if (currentTimeLineTimeoutRef.current) {
      clearTimeout(currentTimeLineTimeoutRef.current);
      currentTimeLineTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledCurrentTimeLineUpdate();
    };
  }, [clearScheduledCurrentTimeLineUpdate]);

  const removeCurrentTimeLines = useCallback((calendarElement = getCalendarElement()) => {
    if (!calendarElement) {
      return;
    }

    calendarElement.querySelectorAll('.current-time-line').forEach((line) => line.remove());
  }, [getCalendarElement]);

  const clearScheduledTimeGridDecorations = useCallback(() => {
    if (timeGridDecorationsTimeoutRef.current) {
      clearTimeout(timeGridDecorationsTimeoutRef.current);
      timeGridDecorationsTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledTimeGridDecorations();
    };
  }, [clearScheduledTimeGridDecorations]);

  const clearScheduledTimeGridStructureAdjustments = useCallback(() => {
    if (timeGridStructureTimeoutRef.current) {
      clearTimeout(timeGridStructureTimeoutRef.current);
      timeGridStructureTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledTimeGridStructureAdjustments();
    };
  }, [clearScheduledTimeGridStructureAdjustments]);

  const clearScheduledStickyHeaderSync = useCallback(() => {
    if (stickyHeaderSyncTimeoutRef.current) {
      clearTimeout(stickyHeaderSyncTimeoutRef.current);
      stickyHeaderSyncTimeoutRef.current = null;
    }

    if (stickyHeaderRafRef.current) {
      cancelAnimationFrame(stickyHeaderRafRef.current);
      stickyHeaderRafRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearScheduledStickyHeaderSync();
    };
  }, [clearScheduledStickyHeaderSync]);

  // 도움말 모달 상태
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const helpIconButtonRef = useRef(null);
  const [helpModalPosition, setHelpModalPosition] = useState({ top: 0, left: 0 });

  const updateHelpModalPosition = useCallback(() => {
    if (!helpIconButtonRef.current) return;

    const rect = helpIconButtonRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const modalWidth = Math.min(300, window.innerWidth - viewportPadding * 2);
    const left = Math.min(
      window.innerWidth - modalWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - modalWidth)
    );

    setHelpModalPosition({
      top: rect.bottom + 8,
      left
    });
  }, []);

  const toggleHelpModal = useCallback(() => {
    if (!helpModalOpen) updateHelpModalPosition();
    setHelpModalOpen(open => !open);
  }, [helpModalOpen, updateHelpModalPosition]);

  useEffect(() => {
    if (!helpModalOpen) return undefined;

    updateHelpModalPosition();
    window.addEventListener('resize', updateHelpModalPosition);
    window.addEventListener('scroll', updateHelpModalPosition, true);

    return () => {
      window.removeEventListener('resize', updateHelpModalPosition);
      window.removeEventListener('scroll', updateHelpModalPosition, true);
    };
  }, [helpModalOpen, updateHelpModalPosition]);

  // 테마 설정 (중앙화된 테마 사용)
  const currentTheme = MODERN_THEME;

  const [maxConcurrent, setMaxConcurrent] = useState(3);

  const [currentViewType, setCurrentViewType] = useState('timeGridWeek');

  // 테마에 맞게 PWA 노치 여백 배경색 설정
  useEffect(() => {
    // theme-color 메타 태그 업데이트
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentTheme.backgroundColor);
    }

    // body 배경색도 테마에 맞게 변경 (PWA 노치 여백 포함)
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = currentTheme.backgroundColor;

    // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8'); // 기본 검은색으로 복원
      }
      document.body.style.backgroundColor = originalBodyBg; // 원래 배경색으로 복원
    };
  }, [currentTheme.backgroundColor]);

  // 모드 상태: 'normal' | 'add' | 'offday'
  const [calendarMode, setCalendarMode] = useState('normal');
  const canEditEvent = useCallback((event) => canEditCalendarEvent({
    calendarMode,
    event,
    userInfo,
  }), [calendarMode, userInfo]);
  const eventMutationScope = getCalendarEventMutationScope(calendarMode, userInfo);

  useEffect(() => {
    requestCalendarRefresh(0, 'calendar.modeChanged');
  }, [calendarMode, requestCalendarRefresh, userInfo?.user_id]);

  // React Query 데이터를 FullCalendar 형식으로 변환 (기존 dayOffEvents 상태 대체)
  const dayOffEvents = React.useMemo(() => {
    if (!rawDayOffData || !Array.isArray(rawDayOffData)) return [];

    return rawDayOffData.map(d => {
      if (!d || !d.day) return null;
      try {
        const dayStr = DateTime.fromISO(d.day, { zone: DEFAULT_TIMEZONE }).toFormat('yyyy-MM-dd');
        if (!dayStr || dayStr === 'Invalid DateTime') return null;
        return {
          id: `dayoff-${dayStr}`,
          title: 'OFF',
          start: dayStr,
          end: dayStr,
          allDay: true,
          display: 'background',
          backgroundColor: 'rgba(120,120,120,0.35)',
          textColor: '#888',
          fontWeight: 300,
          className: 'dayoff-bg-event',
        };
      } catch (error) {
        return null;
      }
    }).filter(item => item !== null);
  }, [rawDayOffData]);

  // refetchDayOff 호환성을 위한 별칭 (기존 코드 호환)
  const refetchDayOff = refetchDayOffQuery;

  // 현재 달력 타이틀 상태
  const [calendarTitle, setCalendarTitle] = useState('');

  // 초기 날짜 상태 (URL 파라미터에서 받아옴)
  const [initialDate, setInitialDate] = useState(new Date());

  // URL 파라미터 처리 플래그
  const [urlProcessed, setUrlProcessed] = useState(false);



  // 모바일 감지 및 이벤트 제한 상태
  const [isMobile, setIsMobile] = useState(false);
  // eventLimits 미사용
  const [, setEventLimits] = useState({});



  // 반짝이는 효과 상태
  const [shiningToday, setShiningToday] = useState(false);


  // 제한설정 모달 상태
  const [showLimitSettings, setShowLimitSettings] = useState(false);

  // 0-8시 시간대 접기/펼치기 상태
  const [isEarlyHoursCollapsed, setIsEarlyHoursCollapsed] = useState(true);

  // 현재 시간 가로선 추가 함수
  const addCurrentTimeLine = useCallback(() => {
    calendarPerf.count('dom.currentTimeLine.calls', 1, { viewType: currentViewType });

    return calendarPerf.measure('dom.currentTimeLine.ms', () => {
      const calendarElement = getCalendarElement();
      if (!calendarElement) {
        calendarPerf.count('dom.currentTimeLine.skipped.noCalendar', 1, { viewType: currentViewType });
        return;
      }

      removeCurrentTimeLines(calendarElement);

      // 주간/일간 뷰에서만 표시
      if (!isTimeGridView(currentViewType)) {
        calendarPerf.count('dom.currentTimeLine.skipped.nonTimeGrid', 1, { viewType: currentViewType });
        return;
      }

      const timeGridBody = calendarElement.querySelector('.fc-timegrid-body');
      if (!timeGridBody) {
        calendarPerf.count('dom.currentTimeLine.skipped.noBody', 1, { viewType: currentViewType });
        return;
      }

      // 현재 시간 계산 (Paris 시간대 기준)
      const now = new Date();
      const parisTime = DateTime.fromJSDate(now).setZone(DEFAULT_TIMEZONE);
      const currentTimeMinutes = (parisTime.hour * 60) + parisTime.minute;

      // 현재 날짜의 열 찾기 (fc-day-today 클래스 사용)
      const currentDayColumn = timeGridBody.querySelector('.fc-timegrid-col.fc-day-today');

      // fc-day-today가 없으면 오늘 날짜가 화면에 없는 것이므로 가로선을 표시하지 않음
      if (!currentDayColumn) {
        calendarPerf.count('dom.currentTimeLine.skipped.noTodayColumn', 1, { viewType: currentViewType });
        return;
      }

      // 열의 실제 위치와 크기 확인
      const columnRect = currentDayColumn.getBoundingClientRect();
      const timeGridRect = timeGridBody.getBoundingClientRect();

      // 현재 날짜 열의 상대적 위치 계산
      const relativeLeft = columnRect.left - timeGridRect.left;

      const timeSlotEntries = Array.from(
        calendarElement.querySelectorAll('.fc-timegrid-slot-lane[data-time]')
      )
        .map((slot) => ({
          slot,
          minutes: parseSlotTimeToMinutes(slot.getAttribute('data-time')),
        }))
        .filter(({ minutes }) => Number.isFinite(minutes));

      calendarPerf.sample('dom.currentTimeLine.slotCount', timeSlotEntries.length, { viewType: currentViewType });

      let targetSlot = null;
      let slotDurationMinutes = currentViewType === 'timeGridWeek30' ? 30 : 60;

      if (timeSlotEntries.length > 1) {
        const derivedDuration = timeSlotEntries[1].minutes - timeSlotEntries[0].minutes;
        if (derivedDuration > 0) {
          slotDurationMinutes = derivedDuration;
        }
      }

      for (let index = 0; index < timeSlotEntries.length; index += 1) {
        const currentSlotMinutes = timeSlotEntries[index].minutes;
        const nextSlotMinutes = timeSlotEntries[index + 1]?.minutes ?? (currentSlotMinutes + slotDurationMinutes);

        if (currentTimeMinutes >= currentSlotMinutes && currentTimeMinutes < nextSlotMinutes) {
          targetSlot = timeSlotEntries[index].slot;
          break;
        }
      }

      if (!targetSlot && timeSlotEntries.length > 0 && currentTimeMinutes >= timeSlotEntries[timeSlotEntries.length - 1].minutes) {
        targetSlot = timeSlotEntries[timeSlotEntries.length - 1].slot;
      }

      let topPosition = 0;
      if (targetSlot) {
        const slotRect = targetSlot.getBoundingClientRect();
        topPosition = slotRect.top - columnRect.top;

        // 슬롯 내에서의 정확한 위치 계산
        const slotStartMinutes = parseSlotTimeToMinutes(targetSlot.getAttribute('data-time')) ?? currentTimeMinutes;
        const minuteOffset = slotDurationMinutes > 0
          ? (currentTimeMinutes - slotStartMinutes) / slotDurationMinutes
          : 0;
        const normalizedOffset = Math.max(0, Math.min(minuteOffset, 1));

        topPosition += normalizedOffset * slotRect.height;
      } else if (timeSlotEntries.length > 0) {
        const visibleStartMinutes = timeSlotEntries[0].minutes;
        const visibleEndMinutes = timeSlotEntries[timeSlotEntries.length - 1].minutes + slotDurationMinutes;

        if (currentTimeMinutes < visibleStartMinutes || currentTimeMinutes > visibleEndMinutes) {
          calendarPerf.count('dom.currentTimeLine.skipped.outsideVisibleRange', 1, { viewType: currentViewType });
          return;
        }

        const timeProgress = (currentTimeMinutes - visibleStartMinutes) / (visibleEndMinutes - visibleStartMinutes);
        topPosition = timeProgress * timeGridBody.offsetHeight;
      } else {
        // 폴백: 단순한 비율 계산
        const dayStartMinutes = currentViewType === 'timeGridWeek30'
          ? 9 * 60
          : (isEarlyHoursCollapsed ? 8 * 60 : 0);
        const dayEndMinutes = 24 * 60;

        if (currentTimeMinutes < dayStartMinutes || currentTimeMinutes > dayEndMinutes) {
          calendarPerf.count('dom.currentTimeLine.skipped.outsideFallbackRange', 1, { viewType: currentViewType });
          return;
        }

        const timeGridHeight = timeGridBody.offsetHeight;
        const timeProgress = (currentTimeMinutes - dayStartMinutes) / (dayEndMinutes - dayStartMinutes);
        topPosition = timeProgress * timeGridHeight;
      }

      // 현재 시간 선 생성
      const currentTimeLine = document.createElement('div');
      currentTimeLine.className = 'current-time-line';

      currentTimeLine.style.cssText = `
      position: absolute !important;
      top: ${topPosition}px !important;
      left: ${relativeLeft}px !important;
      width: ${columnRect.width}px !important;
      height: 1px !important;
      background: #ff69b4 !important;
      border-radius: 1px !important;
      z-index: 9999999 !important;
      pointer-events: none !important;
      animation: currentTimeBlink 2s ease-in-out infinite !important;
      box-shadow: 0 0 8px rgba(255, 105, 180, 0.8) !important;
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
    `;

      // 왼쪽 동그라미 추가
      const circle = document.createElement('div');
      circle.className = 'current-time-circle';
      circle.style.cssText = `
      position: absolute !important;
      left: -2.5px !important;
      top: -2px !important;
      width: 5px !important;
      height: 5px !important;
      background: #ff69b4 !important;
      border-radius: 50% !important;
      border: none !important;
      box-shadow: 0 0 8px rgba(255, 105, 180, 0.9) !important;
      z-index: 10000000 !important;
      animation: currentTimeBlink 2s ease-in-out infinite !important;
    `;

      currentTimeLine.appendChild(circle);
      currentDayColumn.appendChild(currentTimeLine);
      calendarPerf.count('dom.currentTimeLine.applied', 1, { viewType: currentViewType });
    }, { viewType: currentViewType });
  }, [calendarPerf, currentViewType, getCalendarElement, isEarlyHoursCollapsed, removeCurrentTimeLines]);

  const scheduleCurrentTimeLineUpdate = useCallback((delay = 100) => {
    clearScheduledCurrentTimeLineUpdate();
    calendarPerf.count('dom.currentTimeLine.scheduled', 1, { delay, viewType: currentViewType });
    calendarPerf.sample('dom.currentTimeLine.delayMs', delay, { viewType: currentViewType });

    currentTimeLineTimeoutRef.current = setTimeout(() => {
      currentTimeLineTimeoutRef.current = null;
      addCurrentTimeLine();
    }, delay);
  }, [addCurrentTimeLine, calendarPerf, clearScheduledCurrentTimeLineUpdate, currentViewType]);

  // 현재 시간 선 업데이트 (로드 시와 뷰 변경 시)
  useEffect(() => {
    if (!isTimeGridView(currentViewType)) {
      clearScheduledCurrentTimeLineUpdate();
      removeCurrentTimeLines();
      return undefined;
    }

    scheduleCurrentTimeLineUpdate(100);

    const handleResize = () => {
      scheduleCurrentTimeLineUpdate(100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearScheduledCurrentTimeLineUpdate();
      removeCurrentTimeLines();
    };
  }, [clearScheduledCurrentTimeLineUpdate, currentViewType, removeCurrentTimeLines, scheduleCurrentTimeLineUpdate]);



  const removeCustomHoverBox = useCallback(() => {
    const existingHoverBox = document.querySelector('.custom-hover-box');
    if (existingHoverBox) {
      existingHoverBox.remove();
    }
  }, []);

  const attachCustomHoverEffect = useCallback((slot, calendarElement) => {
    if (slot.dataset.customHoverBound === 'true') {
      return;
    }

    slot.dataset.customHoverBound = 'true';

    slot.addEventListener('mouseenter', () => {
      const slotRect = slot.getBoundingClientRect();
      removeCustomHoverBox();

      const calendarRect = calendarElement.getBoundingClientRect();

      const hoverBox = document.createElement('div');
      hoverBox.className = 'custom-hover-box';
      hoverBox.style.cssText = `
        position: absolute !important;
        top: ${slotRect.top - calendarRect.top}px !important;
        left: ${slotRect.left - calendarRect.left}px !important;
        width: ${slotRect.width}px !important;
        height: ${slotRect.height}px !important;
        border: 1px solid #ddd !important;
        background: rgba(0, 0, 0, 0.05) !important;
        pointer-events: none !important;
        z-index: 10001 !important;
        border-radius: 2px !important;
      `;

      calendarElement.appendChild(hoverBox);
    });

    slot.addEventListener('mouseleave', removeCustomHoverBox);
  }, [removeCustomHoverBox]);

  // 커스텀 hover 효과 추가 함수 (1/2 칸 쪼개기)
  const addCustomHoverEffects = useCallback((calendarElement) => {
    if (!calendarElement) {
      return;
    }

    const timeSlots = calendarElement.querySelectorAll('.fc-timegrid-slot');

    timeSlots.forEach(slot => {
      // 점선, 핑크색 가로선, 이벤트가 있는 슬롯은 제외
      if (slot.querySelector('.pink-time-line') ||
        slot.querySelector('.dashed-time-line') ||
        slot.querySelector('.fc-event')) {
        return;
      }

      attachCustomHoverEffect(slot, calendarElement);
    });
  }, [attachCustomHoverEffect]);

  const applyTimeGridDecorations = useCallback(() => {
    calendarPerf.count('dom.decorations.calls', 1, { viewType: currentViewType });

    return calendarPerf.measure('dom.decorations.ms', () => {
      const calendarElement = getCalendarElement();
      if (!calendarElement) {
        calendarPerf.count('dom.decorations.skipped.noCalendar', 1, { viewType: currentViewType });
        return;
      }

      const existingPinkLines = calendarElement.querySelectorAll('.pink-time-line');
      const existingDashedLines = calendarElement.querySelectorAll('.dashed-time-line');
      existingPinkLines.forEach(line => line.remove());
      existingDashedLines.forEach(line => line.remove());

      if (!isTimeGridView(currentViewType)) {
        calendarPerf.count('dom.decorations.skipped.nonTimeGrid', 1, { viewType: currentViewType });
        return;
      }

      addCustomHoverEffects(calendarElement);

      const timeSlots = calendarElement.querySelectorAll('.fc-timegrid-slot');
      calendarPerf.sample('dom.decorations.slotCount', timeSlots.length, { viewType: currentViewType });
      timeSlots.forEach(slot => {
        const dashedLine = document.createElement('div');
        dashedLine.className = 'dashed-time-line';
        slot.style.position = 'relative';
        slot.appendChild(dashedLine);
      });

      const timeLabels = calendarElement.querySelectorAll('.fc-timegrid-slot-label, .fc-timegrid-axis-cushion');
      calendarPerf.sample('dom.decorations.labelCount', timeLabels.length, { viewType: currentViewType });
      timeLabels.forEach(label => {
        const timeText = label.textContent.trim();
        if (!timeText.startsWith(`${CALENDAR_DISPLAY.studioOpeningHour}:`)) {
          return;
        }

        if (currentViewType === 'timeGridDay' && timeText !== `${CALENDAR_DISPLAY.studioOpeningHour}:00`) {
          return;
        }

        label.classList.add('pink-time-label');

        const slot = label.closest('.fc-timegrid-slot');
        if (slot && !slot.classList.contains('fc-timegrid-slot-label')) {
          const pinkLine = document.createElement('div');
          pinkLine.className = 'pink-time-line';
          slot.style.position = 'relative';
          slot.appendChild(pinkLine);
        }
      });

      calendarPerf.count('dom.decorations.applied', 1, { viewType: currentViewType });
    }, { viewType: currentViewType });
  }, [addCustomHoverEffects, calendarPerf, currentViewType, getCalendarElement]);

  const scheduleTimeGridDecorations = useCallback((delay = 250) => {
    clearScheduledTimeGridDecorations();
    calendarPerf.count('dom.decorations.scheduled', 1, { delay, viewType: currentViewType });
    calendarPerf.sample('dom.decorations.delayMs', delay, { viewType: currentViewType });
    timeGridDecorationsTimeoutRef.current = setTimeout(() => {
      timeGridDecorationsTimeoutRef.current = null;
      applyTimeGridDecorations();
    }, delay);
  }, [applyTimeGridDecorations, calendarPerf, clearScheduledTimeGridDecorations, currentViewType]);

  const applyTimeGridStructureAdjustments = useCallback(() => {
    calendarPerf.count('dom.structure.calls', 1, { viewType: currentViewType });

    return calendarPerf.measure('dom.structure.ms', () => {
      const calendarElement = getCalendarElement();
      if (!calendarElement) {
        calendarPerf.count('dom.structure.skipped.noCalendar', 1, { viewType: currentViewType });
        return;
      }

      const headerCells = calendarElement.querySelectorAll('.fc-col-header-cell');
      headerCells.forEach(cell => {
        cell.style.setProperty('border-right', 'none', 'important');
      });

      const timeGridElements = calendarElement.querySelectorAll('.fc-timegrid-col, .fc-timegrid-col-frame, .fc-timegrid-col-bg, .fc-timegrid-col-events');
      timeGridElements.forEach(element => {
        element.style.borderRight = 'none';
      });

      const tableCells = calendarElement.querySelectorAll('.fc-timegrid-table td');
      tableCells.forEach(cell => {
        cell.style.borderRight = 'none';
      });

      const timegridBody = calendarElement.querySelector('.fc-timegrid-body');
      if (timegridBody) {
        const existingDividers = timegridBody.querySelectorAll('.custom-column-divider');
        existingDividers.forEach(divider => divider.remove());
      }

      if (!timegridBody || (currentViewType !== 'timeGridWeek' && currentViewType !== 'timeGridWeek30')) {
        calendarPerf.count('dom.structure.skipped.nonWeekGrid', 1, { viewType: currentViewType });
        return;
      }

      const headerCellsExceptLast = calendarElement.querySelectorAll('.fc-col-header-cell:not(:last-child)');
      headerCellsExceptLast.forEach(cell => {
        cell.style.setProperty('border-right', '1px solid #e0e0e0', 'important');
      });

      const cols = calendarElement.querySelectorAll('.fc-timegrid-col');
      calendarPerf.sample('dom.structure.columnCount', cols.length, { viewType: currentViewType });
      if (cols.length <= 1) {
        calendarPerf.count('dom.structure.skipped.singleColumn', 1, { viewType: currentViewType });
        return;
      }

      const bodyRect = timegridBody.getBoundingClientRect();
      for (let i = 0; i < cols.length - 1; i += 1) {
        const currentColRect = cols[i].getBoundingClientRect();
        const divider = document.createElement('div');
        divider.className = 'custom-column-divider';
        divider.style.cssText = `
          position: absolute !important;
          top: 0 !important;
          bottom: 0 !important;
          left: ${currentColRect.right - bodyRect.left}px !important;
          width: 1px !important;
          background-color: #e0e0e0 !important;
          z-index: 999 !important;
          pointer-events: none !important;
        `;
        timegridBody.appendChild(divider);
      }

      calendarPerf.count('dom.structure.applied', 1, { viewType: currentViewType });
    }, { viewType: currentViewType });
  }, [calendarPerf, currentViewType, getCalendarElement]);

  const scheduleTimeGridStructureAdjustments = useCallback((delay = 200) => {
    clearScheduledTimeGridStructureAdjustments();
    calendarPerf.count('dom.structure.scheduled', 1, { delay, viewType: currentViewType });
    calendarPerf.sample('dom.structure.delayMs', delay, { viewType: currentViewType });
    timeGridStructureTimeoutRef.current = setTimeout(() => {
      timeGridStructureTimeoutRef.current = null;
      applyTimeGridStructureAdjustments();
    }, delay);
  }, [applyTimeGridStructureAdjustments, calendarPerf, clearScheduledTimeGridStructureAdjustments, currentViewType]);

  const ensureStickyHeader = useCallback(() => {
    if (stickyHeaderRef.current) {
      return stickyHeaderRef.current;
    }

    const originalHeader = getCalendarHeaderElement();
    if (!originalHeader) {
      return null;
    }

    const stickyHeader = originalHeader.cloneNode(true);
    stickyHeader.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      z-index: 10000 !important;
      background: #f8f9fa !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
      display: none !important;
      border-radius: 8px !important;
      margin: 0 4px !important;
      pointer-events: none !important;
      width: auto !important;
      max-width: 100vw !important;
      box-sizing: border-box !important;
    `;
    stickyHeader.classList.add('sticky-header');

    const headerCushions = stickyHeader.querySelectorAll('.fc-col-header-cell-cushion');
    headerCushions.forEach(cushion => {
      cushion.style.fontWeight = '400';
    });

    const stickyTable = stickyHeader.querySelector('table');
    if (stickyTable) {
      stickyTable.style.width = '100%';
      stickyTable.style.tableLayout = 'fixed';
    }

    document.body.appendChild(stickyHeader);
    stickyHeaderRef.current = stickyHeader;
    return stickyHeader;
  }, [getCalendarHeaderElement]);

  const syncStickyHeader = useCallback(() => {
    calendarPerf.count('stickyHeader.calls', 1, { viewType: currentViewType });

    return calendarPerf.measure('stickyHeader.ms', () => {
      const originalHeader = getCalendarHeaderElement();
      if (!originalHeader) {
        if (stickyHeaderRef.current) {
          stickyHeaderRef.current.style.display = 'none';
        }
        calendarPerf.count('stickyHeader.skipped.noHeader', 1, { viewType: currentViewType });
        return;
      }

      const stickyHeader = ensureStickyHeader();
      if (!stickyHeader) {
        calendarPerf.count('stickyHeader.skipped.noStickyNode', 1, { viewType: currentViewType });
        return;
      }

      const originalCushions = originalHeader.querySelectorAll('.fc-col-header-cell-cushion');
      const stickyCushions = stickyHeader.querySelectorAll('.fc-col-header-cell-cushion');
      originalCushions.forEach((originalCushion, index) => {
        if (stickyCushions[index]) {
          const textContent = originalCushion.textContent || originalCushion.innerText;
          if (stickyCushions[index].textContent !== textContent) {
            stickyCushions[index].textContent = textContent;
          }
        }
      });

      const rect = originalHeader.getBoundingClientRect();
      const wrapperRect = calendarWrapperRef.current?.getBoundingClientRect() || null;

      if (rect.top <= 0) {
        stickyHeader.style.display = 'block';
        if (wrapperRect) {
          stickyHeader.style.left = `${wrapperRect.left}px`;
          stickyHeader.style.width = `${wrapperRect.width}px`;
        } else {
          stickyHeader.style.left = `${rect.left}px`;
          stickyHeader.style.width = `${rect.width}px`;
        }
        calendarPerf.count('stickyHeader.visible', 1, { viewType: currentViewType });
        return;
      }

      stickyHeader.style.display = 'none';
      calendarPerf.count('stickyHeader.hidden', 1, { viewType: currentViewType });
    }, { viewType: currentViewType });
  }, [calendarPerf, currentViewType, ensureStickyHeader, getCalendarHeaderElement]);

  const scheduleStickyHeaderSync = useCallback((delay = 0) => {
    clearScheduledStickyHeaderSync();
    calendarPerf.count('stickyHeader.scheduled', 1, { delay, viewType: currentViewType });
    calendarPerf.sample('stickyHeader.delayMs', delay, { viewType: currentViewType });

    const runSync = () => {
      stickyHeaderRafRef.current = requestAnimationFrame(() => {
        stickyHeaderRafRef.current = null;
        syncStickyHeader();
      });
    };

    if (delay > 0) {
      stickyHeaderSyncTimeoutRef.current = setTimeout(() => {
        stickyHeaderSyncTimeoutRef.current = null;
        runSync();
      }, delay);
      return;
    }

    runSync();
  }, [calendarPerf, clearScheduledStickyHeaderSync, currentViewType, syncStickyHeader]);

  // 0-8시 시간대 토글 함수
  const toggleEarlyHours = useCallback(() => {
    setIsEarlyHoursCollapsed(!isEarlyHoursCollapsed);

    // FullCalendar API를 통해 시간 범위 업데이트
    const api = calendarRef.current?.getApi();
    if (api) {
      const newSlotMinTime = isEarlyHoursCollapsed ? '00:00:00' : '08:00:00';
      api.setOption('slotMinTime', newSlotMinTime);

      // 부드러운 스크롤 효과
      setTimeout(() => {
        api.setOption('scrollTime', '08:00:00');

        if (isTimeGridView(currentViewType)) {
          scheduleTimeGridDecorations(250);
        }
      }, 100);
    }
  }, [currentViewType, isEarlyHoursCollapsed, scheduleTimeGridDecorations]);

  const mapBlockedEvents = useCallback((eventBlocks = []) => {
    return eventBlocks.map(block => {
      const mappedBlock = toRestrictedPeriodCalendarEvent(block, {
        interactive: calendarMode === 'admin',
        label: t('restrictedPeriodLabel', lang),
      });

      if (!mappedBlock) {
        console.warn('[CALENDAR] 유효하지 않은 블록 데이터 필터링됨:', {
          block_id: block?.id,
          start_time: block?.start_time,
          end_time: block?.end_time
        });
      }

      return mappedBlock;
    }).filter(Boolean);
  }, [calendarMode, lang]);

  const buildSafeCalendarEvents = useCallback((...eventGroups) => {
    return eventGroups
      .flat()
      .filter(event => event && typeof event.id !== 'undefined' && event.id !== null);
  }, []);

  const fetchBlockedEvents = useCallback(async (startISO, endISO) => {
    try {
      const blockData = await loadPublicEventBlocks(startISO, endISO);

      return mapBlockedEvents(blockData?.eventBlocks || []);
    } catch (error) {
      return [];
    }
  }, [mapBlockedEvents]);

  useEffect(() => {
    loadMaximumConcurrentLimit()
      .then(setMaxConcurrent)
      .catch(() => {
        // 캘린더 설정 로드 실패
      });
  }, []);

  // 모바일 감지 및 이벤트 제한 로직
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (!mobile) {
        setEventLimits({});
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sticky 헤더 강화를 위한 스크롤 이벤트 리스너
  useEffect(() => {
    scheduleStickyHeaderSync(180);

    const handleScroll = () => {
      scheduleStickyHeaderSync();
    };

    const handleResize = () => {
      scheduleStickyHeaderSync(60);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearScheduledStickyHeaderSync();

      if (stickyHeaderRef.current) {
        stickyHeaderRef.current.remove();
        stickyHeaderRef.current = null;
      }
    };
  }, [clearScheduledStickyHeaderSync, currentViewType, scheduleStickyHeaderSync]);

  // 기존 refetchDayOff 함수 및 useEffect는 React Query (useDayOffsQuery)로 대체됨
  // 데이터는 자동으로 캐싱되고 관리됨


  // 일정 개수 계산 함수


  // 서버에서 전체 이벤트 fetch 함수
  const fetchEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    const requestId = ++fetchEventsRequestIdRef.current;
    const fetchToken = calendarPerf.start('fetchEvents.totalMs', {
      requestId,
      viewType: currentViewType,
    });
    let fetchSettled = false;

    const isStaleRequest = () => requestId !== fetchEventsRequestIdRef.current;
    const finishFetch = (status, eventCount = 0, meta = {}) => {
      if (fetchSettled) {
        return;
      }

      fetchSettled = true;
      calendarPerf.count(`fetchEvents.status.${status}`, 1, {
        requestId,
        viewType: currentViewType,
        ...meta,
      });
      calendarPerf.sample('fetchEvents.eventCount', eventCount, {
        requestId,
        status,
        viewType: currentViewType,
        ...meta,
      });
      calendarPerf.end(fetchToken, {
        requestId,
        status,
        eventCount,
        viewType: currentViewType,
        ...meta,
      });
    };

    // ISO8601(초, 밀리초 포함)로 변환
    const startISO = new Date(fetchInfo.start).toISOString();
    const endISO = new Date(fetchInfo.end).toISOString();
    const rangeDays = Math.round((fetchInfo.end.getTime() - fetchInfo.start.getTime()) / (1000 * 60 * 60 * 24));
    calendarPerf.count('fetchEvents.calls', 1, {
      requestId,
      viewType: currentViewType,
      rangeDays,
    });
    calendarPerf.sample('fetchEvents.rangeDays', rangeDays, {
      requestId,
      viewType: currentViewType,
    });
    const blockedEventsPromise = fetchBlockedEvents(startISO, endISO);

    // 로그 제거하여 성능 개선

    // 1. 일반 일정 불러오기
    apiCallJson(`/api/events?start_time=${startISO}&end_time=${endISO}`)
      .then(data => {
        // 로그 제거하여 성능 개선

        if (!data) {
          // API 호출 실패 시 빈 배열로 처리하고 계속 진행
          if (isDev()) console.log('[CALENDAR] 일반 일정 API 실패, 빈 배열로 처리');
          return { events: [] };
        }

        setSafetyEvents((data.events || []).filter(event=>event.created_by && event.created_by!==userInfo?.user_id));
        const mappedEvents = (data.events || []).map(event => {
          // 기본 데이터 유효성 검사
          if (!event || !event.id || !event.start_time || !event.end_time) {
            return null;
          }

          const startDate = new Date(event.start_time);
          const endDate = new Date(event.end_time);

          // Date 객체 유효성 검사
          if (!(startDate instanceof Date) || isNaN(startDate.getTime()) ||
            !(endDate instanceof Date) || isNaN(endDate.getTime()) ||
            startDate >= endDate) {
            return null; // 유효하지 않은 데이터는 null 반환
          }

          const calendarEvent = toCalendarEvent(event);
          if (!calendarEvent) {
            return null;
          }

          const isEditable = canEditEvent(calendarEvent);
          calendarEvent.startEditable = isEditable;
          calendarEvent.durationEditable = isEditable;

          // 게스트 이벤트 스타일링
          if (event.calendar_type === 'guest') {
            // 게스트 이름: user_name, created_by_name, guest_name 순서로 시도
            const guestName = event.user_name || event.created_by_name || event.guest_name || '';
            calendarEvent.title = guestName ? `GUEST/${guestName}` : 'GUEST';
            calendarEvent.classNames = ['guest-event'];
            calendarEvent.extendedProps = {
              ...calendarEvent.extendedProps,
              isGuestEvent: true,
              calendarType: 'guest',
              guestArtistId: event.guest_artist_id,
              originalTitle: event.title
            };
          }

          // BIDON 이벤트 스타일링 (타이틀은 유지)
          if (event.calendar_type === 'bidon') {
            calendarEvent.classNames = ['bidon-event'];
            calendarEvent.extendedProps = {
              ...calendarEvent.extendedProps,
              isBidonEvent: true,
              calendarType: 'bidon'
            };
          }

          return calendarEvent;
        }).filter(event => event !== null); // null 값 제거

        blockedEventsPromise
          .then((mappedBlocks) => {
            if (isStaleRequest()) {
              finishFetch('stale.afterBlocks', 0, { mappedEventCount: mappedEvents.length });
              return;
            }

            fetchEventsRetryCountRef.current = 0;
            const finalEvents = buildSafeCalendarEvents(
              mappedEvents,
              mappedBlocks,
              dayOffEvents || []
            );

            calendarPerf.sample('fetchEvents.blockedEventCount', mappedBlocks.length, {
              requestId,
              viewType: currentViewType,
            });
            finishFetch('success', finalEvents.length, {
              mappedEventCount: mappedEvents.length,
              blockedEventCount: mappedBlocks.length,
              dayOffEventCount: dayOffEvents?.length || 0,
            });
            successCallback(finalEvents);
          })
          .catch(() => {
            if (isStaleRequest()) {
              finishFetch('stale.afterBlockedFetchError', 0, { mappedEventCount: mappedEvents.length });
              return;
            }

            fetchEventsRetryCountRef.current = 0;
            const finalEvents = buildSafeCalendarEvents(
              mappedEvents,
              dayOffEvents || []
            );

            finishFetch('successWithoutBlockedEvents', finalEvents.length, {
              mappedEventCount: mappedEvents.length,
              dayOffEventCount: dayOffEvents?.length || 0,
            });
            successCallback(finalEvents);
          });
      })
      .catch(error => {
        if (isStaleRequest()) {
          finishFetch('stale.fetchError');
          return;
        }

        console.error('[CALENDAR] Fetch events failed:', error);

        // 토큰 만료 체크 - 하지만 강제 리다이렉트하지 않고 빈 배열 반환
        if (error.message.includes('401') || error.message.includes('403')) {
          if (isDev()) console.log('[CALENDAR] 인증 오류 발생, 빈 배열 반환');
          fetchEventsRetryCountRef.current = 0;
          finishFetch('authError', 0);
          successCallback([]);
          return;
        }

        // 재시도 로직 (최대 2번으로 줄임)
        if (fetchEventsRetryCountRef.current < 2) {
          fetchEventsRetryCountRef.current += 1;
          calendarPerf.count('fetchEvents.retryScheduled', 1, {
            requestId,
            attempt: fetchEventsRetryCountRef.current,
            viewType: currentViewType,
          });
          if (isDev()) console.log(`[CALENDAR] 재시도 ${fetchEventsRetryCountRef.current}/2`);

          finishFetch('retryScheduled', 0, {
            attempt: fetchEventsRetryCountRef.current,
          });
          setTimeout(() => {
            if (isStaleRequest()) {
              return;
            }

            fetchEvents(fetchInfo, successCallback, failureCallback);
          }, 1000 * fetchEventsRetryCountRef.current); // 1초, 2초 간격으로 재시도
          return;
        }

        // 최대 재시도 횟수 초과 - 빈 배열 반환
        if (isDev()) console.log('[CALENDAR] 최대 재시도 횟수 초과, 빈 배열 반환');
        fetchEventsRetryCountRef.current = 0;
        finishFetch('failedAfterRetries', 0);
        successCallback([]);
      });
  }, [buildSafeCalendarEvents, calendarPerf, canEditEvent, currentViewType, dayOffEvents, fetchBlockedEvents, userInfo?.user_id]);

  useEffect(() => {
    const socketUrl = getSocketOrigin();
    const socket = io(socketUrl, {
      withCredentials: true,
      timeout: 10000, // 10초 타임아웃
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socket.on('connect', () => {
      // Connected
    });

    socket.on('disconnect', (reason) => {
      // Disconnected
    });

    socket.on('connect_error', (error) => {
      console.error('[SOCKET] Connection error:', error);
    });

    socket.on('initialEvents', () => {
      requestCalendarRefresh(200, 'socket.initialEvents');
    });
    socket.on('eventAdded', () => {
      requestCalendarRefresh(200, 'socket.eventAdded');
    });
    socket.on('eventUpdated', () => {
      requestCalendarRefresh(200, 'socket.eventUpdated');
    });
    socket.on('eventDeleted', () => {
      requestCalendarRefresh(200, 'socket.eventDeleted');
    });

    return () => {
      clearScheduledCalendarRefresh();
      socket.disconnect();
    };
  }, [clearScheduledCalendarRefresh, requestCalendarRefresh]);

  // 페이지 포커스 시 이벤트 다시 불러오기 (모바일에서 앱 전환 후 돌아올 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        requestCalendarRefresh(200, 'document.visible');
      }
    };

    const handleFocus = () => {
      requestCalendarRefresh(200, 'window.focus');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [requestCalendarRefresh]);

  // 모달 열림 상태
  const [modalOpen, setModalOpen] = useState(false);
  // 선택한 시작 날짜 상태
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  // 선택한 종료 날짜 상태
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  // 상태 추가
  const [selectedStartTime, setSelectedStartTime] = useState('09:00');
  const [selectedEndTime, setSelectedEndTime] = useState('10:00');

  // 여기 추가
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [restrictedPeriodModalOpen, setRestrictedPeriodModalOpen] = useState(false);
  const [selectedRestrictedPeriod, setSelectedRestrictedPeriod] = useState(null);
  const [restrictedPeriodSaving, setRestrictedPeriodSaving] = useState(false);
  const studioUnavailableAlertAtRef = useRef(0);

  const showStudioUnavailableAlert = useCallback((restrictedPeriods = []) => {
    const now = Date.now();
    if (now - studioUnavailableAlertAtRef.current < STUDIO_UNAVAILABLE_ALERT_COOLDOWN_MS) {
      return;
    }

    const message = formatStudioUnavailableMessage(restrictedPeriods, lang)
      || t('studioUnavailable', lang);
    alert(message);
    // alert가 닫힌 직후 이어서 실행되는 dateClick/select 중복 안내를 차단합니다.
    studioUnavailableAlertAtRef.current = Date.now();
  }, [lang]);

  const closeRestrictedPeriodModal = useCallback(() => {
    if (restrictedPeriodSaving) {
      return;
    }

    setRestrictedPeriodModalOpen(false);
    setSelectedRestrictedPeriod(null);
  }, [restrictedPeriodSaving]);

  const handleRestrictedPeriodSave = async (payload) => {
    const start = new Date(payload.start);
    const end = new Date(payload.end);

    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
      alert(t('endTimeAfterStart', lang));
      return;
    }

    const calendarApi = getCalendarApi();
    const otherEvents = (calendarApi ? calendarApi.getEvents() : []).filter(
      event => String(event.id) !== String(selectedRestrictedPeriod?.id)
    );

    if (hasRestrictedPeriodOverlap(start, end, otherEvents)) {
      alert(t('blockOverlap', lang));
      return;
    }

    const requestBody = {
      start_time: payload.start,
      end_time: payload.end,
      reason: payload.reason || '관리자 설정 금지기간',
      force_add: true,
    };

    setRestrictedPeriodSaving(true);
    try {
      if (selectedRestrictedPeriod?.id) {
        const blockId = String(selectedRestrictedPeriod.id).replace('block-', '');
        await eventBlockAPI.update(blockId, requestBody);
        alert(t('blockUpdated', lang));
      } else {
        await eventBlockAPI.create(requestBody);
        alert(t('blockAdded', lang));
      }

      setRestrictedPeriodModalOpen(false);
      setSelectedRestrictedPeriod(null);
      requestCalendarRefresh(200, 'admin.blockSaved');
    } catch (error) {
      alert(t(selectedRestrictedPeriod?.id ? 'blockUpdateError' : 'blockAddError', lang));
    } finally {
      setRestrictedPeriodSaving(false);
    }
  };

  const handleRestrictedPeriodDelete = async (eventId) => {
    const blockId = String(eventId).replace('block-', '');

    setRestrictedPeriodSaving(true);
    try {
      await eventBlockAPI.delete(blockId);
      setRestrictedPeriodModalOpen(false);
      setSelectedRestrictedPeriod(null);
      requestCalendarRefresh(200, 'admin.blockRemoved');
      alert(t('blockRemoved', lang));
    } catch (error) {
      alert(t('blockDeleteError', lang));
    } finally {
      setRestrictedPeriodSaving(false);
    }
  };

  // 날짜 클릭 시 모달 열기 (dayGrid, timeGrid 모두 대응)
  const handleDateClick = async (arg) => {
    try {
      // 사용자 정보 확인
      if (!userInfo) {
        alert(t('userInfoError', lang));
        return;
      }

      const clickedDate = DateTime.fromJSDate(arg.date).setZone(DEFAULT_TIMEZONE);
      const isEventElementClick = isFullCalendarEventElementClick(arg.jsEvent);

      if (calendarMode === 'admin' && isEventElementClick) {
        return;
      }

      if (isCalendarEventCreationMode(calendarMode)) {
        // 이벤트 클릭은 handleEventClick 한 곳에서만 처리해 배경 셀 dateClick과 중복되지 않게 합니다.
        if (isEventElementClick) {
          return;
        }

        const calendarApi = getCalendarApi();
        const allEvents = calendarApi ? calendarApi.getEvents() : [];
        const rangeStart = arg.view.type === 'dayGridMonth'
          ? clickedDate.startOf('day').toJSDate()
          : arg.date;
        const rangeEnd = arg.view.type === 'dayGridMonth'
          ? clickedDate.startOf('day').plus({ days: 1 }).toJSDate()
          : new Date(arg.date.getTime() + DEFAULT_CLICK_EVENT_DURATION_MS);

        const blockingRestrictedPeriods = findBlockingRestrictedPeriods({
          calendarMode,
          rangeStart,
          rangeEnd,
          events: allEvents,
          fullDayOnly: arg.view.type === 'dayGridMonth',
        });

        if (blockingRestrictedPeriods.length > 0) {
          showStudioUnavailableAlert(blockingRestrictedPeriods);
          return;
        }
      }

      // 월간 뷰에서 날짜 클릭 시 일간 뷰로 전환 (일반 모드와 추가 모드에서)
      if (arg.view.type === 'dayGridMonth' && (calendarMode === 'normal' || calendarMode === 'add' || calendarMode === 'admin')) {
        try {
          const calendarApi = getCalendarApi();
          if (calendarApi) {
            calendarApi.changeView('timeGridDay', clickedDate.toJSDate());
          }
        } catch (error) {
          // 뷰 전환 오류
        }

        return;
      }

      // OFFDAY 모드에서만 dayoff 처리
      if (calendarMode === 'offday') {
        const clickedDate = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd');
        // 이미 dayoff로 지정된 칸 클릭 시 자동 삭제
        const isDayOff = dayOffEvents.some(ev => ev.start === clickedDate);
        if (isDayOff) {
          apiClient.delete(`/artist-dayoff?artist_id=${userInfo.user_id}&day=${clickedDate}`)
            .then(() => refetchDayOff())
            .catch(() => {
              alert(t('dayOffDeleteError', lang));
            });
          return; // 삭제 후 종료
        } else {
          // dayoff가 아니면 등록
          apiClient.post('/artist-dayoff', { artist_id: userInfo.user_id, day: clickedDate, status: 'DayOff' })
            .then(() => refetchDayOff())
            .catch(() => {
              alert(t('dayOffAddError', lang));
            });
          return;
        }
      }

      // ADD 모드에서는 이벤트 클릭만 방지 (날짜 클릭은 모달 열기 허용)
      // 이 부분은 handleEventClick에서 처리하므로 여기서는 제거

      // 일간/주간 뷰에서만 모달 열기 (일반 모드와 추가 모드에서)
      if ((calendarMode === 'normal' || calendarMode === 'add') && (arg.view.type === 'timeGridDay' || arg.view.type === 'timeGridWeek')) {
        // 모바일에서는 클릭 지속 시간 체크를 완화하거나 제거
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
          // 데스크톱에서만 드래그 선택과 구분하기 위해 짧은 클릭만 처리 (400ms 기준으로 조정)
          const clickDuration = Date.now() - (arg.jsEvent?.timeStamp || Date.now());
          if (clickDuration > 400) {
            return;
          }
        }
        const d = DateTime.fromJSDate(arg.date).setZone(DEFAULT_TIMEZONE);
        const dateStr = d.toFormat('yyyy-MM-dd');

        // 휴무일 체크 제거 - OFF 날에도 이벤트 추가/수정 가능

        const timeStr = d.toFormat('HH:mm');
        setSelectedStartDate(dateStr);
        setSelectedStartTime(timeStr);
        setSelectedEndDate(dateStr);
        setSelectedEndTime(DateTime.fromJSDate(new Date(arg.date.getTime() + DEFAULT_CLICK_EVENT_DURATION_MS)).setZone(DEFAULT_TIMEZONE).toFormat('HH:mm'));
        setModalOpen(true);
        setCurrentViewType(arg.view.type);
      }
    } catch (error) {
      alert('날짜 클릭 처리 중 오류가 발생했습니다.');
    }
  };

  // 드래그 선택 시 모달 열기 (여러 셀 선택)
  const handleDateSelect = (selectInfo) => {
    if (calendarMode === 'offday') {
      // Off-day 모드에서는 드래그 선택 비활성화
      selectInfo.view.calendar.unselect();
      return;
    }

    if (calendarMode === 'admin') {
      const calendarApi = calendarRef.current?.getApi();
      const allEvents = calendarApi ? calendarApi.getEvents() : [];

      if (hasRestrictedPeriodOverlap(selectInfo.start, selectInfo.end, allEvents)) {
        alert(t('blockOverlap', lang));
        selectInfo.view.calendar.unselect();
        return;
      }

      const isAllDay = Boolean(selectInfo.allDay || selectInfo.view.type === 'dayGridMonth');
      setSelectedRestrictedPeriod({
        id: null,
        reason: '관리자 설정 금지기간',
        start: toRestrictedPeriodInputValue(selectInfo.start, isAllDay),
        end: toRestrictedPeriodInputValue(selectInfo.end, isAllDay),
        allDay: isAllDay,
        is_restricted_period: true,
      });
      setRestrictedPeriodModalOpen(true);
      setCurrentViewType(selectInfo.view.type);
      selectInfo.view.calendar.unselect();
      return;
    }

    // 추가 모드에서는 드래그 선택 활성화 (일정 추가용)
    if (calendarMode === 'add') {
      // 추가 모드에서도 드래그 선택으로 일정 추가 가능
    }

    // 월간 뷰에서는 드래그 선택 무시
    if (selectInfo.view.type === 'dayGridMonth') {
      selectInfo.view.calendar.unselect();
      return;
    }

    const startDate = DateTime.fromJSDate(selectInfo.start).setZone(DEFAULT_TIMEZONE);
    const endDate = DateTime.fromJSDate(selectInfo.end).setZone(DEFAULT_TIMEZONE);

    // 시작 날짜와 시간
    const startDateStr = startDate.toFormat('yyyy-MM-dd');
    const startTimeStr = startDate.toFormat('HH:mm');

    // 종료 날짜와 시간 (정확한 시간 표시)
    const endDateStr = endDate.toFormat('yyyy-MM-dd');
    const endTimeStr = endDate.toFormat('HH:mm');

    // 휴무일 체크 제거 - OFF 날에도 이벤트 추가/수정 가능

    // 선택 구간이 금지 기간과 조금이라도 겹치면 일정 생성 차단
    const calendarApi = calendarRef.current?.getApi();
    const allEvents = calendarApi ? calendarApi.getEvents() : [];
    const blockingRestrictedPeriods = findBlockingRestrictedPeriods({
      calendarMode,
      rangeStart: selectInfo.start,
      rangeEnd: selectInfo.end,
      events: allEvents,
    });
    if (blockingRestrictedPeriods.length > 0) {
      showStudioUnavailableAlert(blockingRestrictedPeriods);
      selectInfo.view.calendar.unselect();
      return;
    }

    setSelectedStartDate(startDateStr);
    setSelectedStartTime(startTimeStr);
    setSelectedEndDate(endDateStr);
    setSelectedEndTime(endTimeStr);
    setModalOpen(true);
    setCurrentViewType(selectInfo.view.type);

    // 선택 해제
    selectInfo.view.calendar.unselect();
  };

  // 수정: 이벤트 클릭 시
  const handleEventClick = (arg) => {
    // 금지기간은 ADMIN에서 관리하고, 일정 생성 모드에서는 이용 불가 안내
    if (arg.event.extendedProps?.is_restricted_period) {
      arg.jsEvent?.stopPropagation();

      const isAllDay = isFullDayRestrictedPeriodEvent(arg.event);
      if (calendarMode === 'admin') {
        const restrictedStart = isAllDay
          ? arg.event.start
          : (arg.event.extendedProps?.restrictedStart ?? arg.event.start);
        const restrictedEnd = isAllDay
          ? arg.event.end
          : (arg.event.extendedProps?.restrictedEnd ?? arg.event.end);

        setSelectedRestrictedPeriod({
          id: arg.event.id,
          reason: arg.event.extendedProps?.reason || '관리자 설정 금지기간',
          start: toRestrictedPeriodInputValue(restrictedStart, isAllDay),
          end: toRestrictedPeriodInputValue(restrictedEnd, isAllDay),
          allDay: isAllDay,
          is_restricted_period: true,
        });
        setRestrictedPeriodModalOpen(true);
        return;
      }

      if (isCalendarEventCreationMode(calendarMode)) {
        if (arg.view?.type === 'dayGridMonth' && !isAllDay) {
          const calendarApi = getCalendarApi();
          calendarApi?.changeView('timeGridDay', arg.event.start);
          return;
        }

        showStudioUnavailableAlert([arg.event]);
      }
      return;
    }

    // 추가 모드에서는 이벤트 클릭 방지
    if (calendarMode === 'add') {
      return;
    }

    if (!canEditEvent(arg.event)) {
      return;
    }

    arg.jsEvent.stopPropagation();
    setSelectedEvent({
      id: arg.event.id,
      title: arg.event.title,
      start: arg.event.start.toISOString(),  // UTC ISO 문자열로 전달 (EditEventModal에서 타임존 변환)
      end: arg.event.end.toISOString(),      // UTC ISO 문자열로 전달
      allDay: arg.event.extendedProps?.is_all_day ?? arg.event.allDay,
      createdBy: arg.event.extendedProps?.createdBy,
    });
    setEditModalOpen(true);
  };

  // 수정: 수정된 이벤트 상태 반영
  const handleEventUpdated = (updatedEvent) => {


    // 이벤트 데이터 유효성 검사
    if (!updatedEvent || !updatedEvent.id) {
      // 수정된 이벤트에 id가 없음
    }

    setEditModalOpen(false);
  };

  // 삭제 후 소켓에서 반영될 예정
  const handleEventDeleted = () => {

    setEditModalOpen(false); // 모달만 닫기
  };

  // 새 이벤트 추가 후, 소켓에서 반영될 예정
  const handleEventAdded = () => {

    setModalOpen(false); // 모달만 닫기
  };

  // 드래그&드롭 등으로 이벤트 이동 시 호출되는 함수
  const handleEventDrop = async (info) => {
    const event = info.event;

    if (!canEditEvent(event)) {
      alert(t('noPermission', lang));
      info.revert();
      return;
    }

    const updatedEvent = {
      id: event.id,
      title: event.title,
      start: toParisISOString(event.start),
      end: toParisISOString(event.end),
      is_all_day: event.allDay,
    };

    // 로딩 상태 즉시 표시 (사용자 피드백 개선)
    const originalTitle = event.title;
    event.setProp('title', `${originalTitle} (${t('updatingEvent', lang)})`);

    try {
      // 동시일정 제한 체크를 백그라운드에서 비동기로 처리
      const calendarApi = getCalendarApi();
      const allEvents = calendarApi ? calendarApi.getEvents() : [];
      const newStart = updatedEvent.start;
      const newEnd = updatedEvent.end;
      const excludeId = updatedEvent.id || null;

      // 요청 데이터 준비 (API 호출 전에 먼저 준비)
      const requestBody = {
        title: updatedEvent.title,
        start_time: toUTCISOStringFromLocalInput(updatedEvent.start, updatedEvent.is_all_day),
        end_time: toUTCISOStringFromLocalInput(updatedEvent.end, updatedEvent.is_all_day),
        is_all_day: updatedEvent.is_all_day,
      };

      // 필수 필드 검증
      if (!requestBody.start_time || !requestBody.end_time) {
        alert(t('startEndTimeRequired', lang));
        info.revert();
        return;
      }

      // 시간 순서 검증
      if (new Date(requestBody.start_time) >= new Date(requestBody.end_time)) {
        alert(t('endTimeAfterStart', lang));
        info.revert();
        return;
      }

      // 서버 저장 전에 프런트에서 먼저 제한 검증
      const overlappingResult = await isOverlappingLimit(newStart, newEnd, allEvents, excludeId, maxConcurrent, lang);
      if (overlappingResult.isOverlapping) {
        alert(overlappingResult.description || t('overlappingLimit', lang, { limit: overlappingResult.limit }));
        event.setProp('title', originalTitle);
        info.revert();
        return;
      }

      const mutationPath = getCalendarEventMutationPath(eventMutationScope, event.id);
      const result = await apiClient.put(mutationPath, requestBody);

      // 성공 시 이벤트 정보 업데이트
      if (result.event) {
        event.setProp('title', result.event.title);
        event.setStart(result.event.start_time);
        event.setEnd(result.event.end_time);
      } else {
        // 백엔드 응답에 event가 없는 경우 원래 제목으로 복원
        event.setProp('title', originalTitle);
      }

      // 성공 시 캘린더 새로고침 제거 (성능 최적화)
      // FullCalendar가 자동으로 이벤트 상태를 업데이트하므로 불필요

    } catch (err) {
      // 로딩 상태 제거
      event.setProp('title', event.title.replace(` (${t('updatingEvent', lang)})`, ''));

      // 사용자에게 에러 알림 (다국어 처리)
      let errorMessage = t('eventUpdateError', lang);
      if (err.message && err.message !== t('unknownError', lang)) {
        // 백엔드에서 오는 하드코딩된 메시지를 다국어로 변환
        const backendMessage = err?.data?.message || err?.data?.error || err.message;
        if (err?.data?.code === 'CALENDAR_CAPACITY') {
          errorMessage = t('concurrentLimitExceeded', lang, { limit: err.data.limit ?? maxConcurrent });
        } else if (backendMessage.includes('동시일정제한 초과')) {
          errorMessage = t('concurrentLimitExceeded', lang, { limit: maxConcurrent });
        } else if (backendMessage.includes('동시일정 제한 초과')) {
          errorMessage = t('concurrentLimitExceeded', lang, { limit: maxConcurrent });
        } else {
          errorMessage = backendMessage;
        }
      }
      alert(errorMessage);

      // 캘린더 새로고침하여 원래 상태로 복원
      requestCalendarRefresh(200, 'event.updateFailed');
    }
  };

  // 현재 미사용 핸들러
  // eslint-disable-next-line no-unused-vars
  const handleModalSave = (payload) => {
    apiClient.post('/admin/events', payload)
      .then(() => {
        setModalOpen(false);
        setSelectedEvent(null);
      })
      .catch((err) => {
        alert('Failed to save event: ' + err.message);
      });
  };

  // 삭제(모달에서) 시에도 권한 체크 필요 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const handleModalDelete = (eventId) => {
    if (!canEditEvent(selectedEvent)) {
      alert(t('noPermission', lang));
      return;
    }
    apiClient.delete(getCalendarEventMutationPath(eventMutationScope, eventId))
      .then(() => {
        setModalOpen(false);
        setSelectedEvent(null);
      })
      .catch(() => alert('Failed to delete event'));
  };

  // 이벤트 클래스명 설정
  const eventClassNames = useCallback((arg) => {
    const classNames = [];

    // DayOff 이벤트
    if (arg.event.title === 'OFF') {
      classNames.push('dayoff-bg-event');
    }

    // 제한 기간 이벤트
    if (arg.event.extendedProps?.is_restricted_period) {
      classNames.push('restricted-period-event');
    }

    if (shouldPassCalendarEventClickThrough({
      calendarMode,
      event: arg.event,
      userInfo,
    })) {
      classNames.push('calendar-event-click-through');
    }

    return classNames;
  }, [calendarMode, userInfo]);

  // 요일 헤더 커스텀 렌더러 - "Mon 26/01" 형식으로 표시 (파리 시간대 기준)
  const renderDayHeaderContent = useCallback((arg) => {
    // 파리 시간대 기준으로 날짜 정보 추출 (사용자 로컬 타임존 영향 제거)
    const parisDT = DateTime.fromJSDate(arg.date).setZone(DEFAULT_TIMEZONE);
    const day = String(parisDT.day).padStart(2, '0');
    const month = String(parisDT.month).padStart(2, '0');

    // 월간 뷰에서는 요일만 표시
    if (arg.view.type === 'dayGridMonth') {
      return arg.text;
    }

    // 주간/일간 뷰에서는 "요일 일/월" 형식으로 표시 (파리 시간대 기준)
    const locale = lang === 'ko' ? 'ko' : lang === 'fr' ? 'fr' : 'en';
    const weekdayShort = parisDT.setLocale(locale).toFormat('ccc');
    return `${weekdayShort} ${day}/${month}`;
  }, [lang]);

  // 이벤트 커스텀 렌더러
  const renderEventContent = useCallback((eventInfo) => {
    const { event, view } = eventInfo;

    if (event.extendedProps?.is_restricted_period && event.display === 'background') {
      // 아무것도 표시하지 않음 (X 텍스트 제거)
      return null;
    }

    // DayOff 이벤트는 기존 텍스트 표시 유지
    if (event.title === 'OFF') {
      return <CalendarDayOffEventContent title={event.title} />;
    }

    // 월간 뷰에서만 동그라미 표시
    if (view.type === 'dayGridMonth') {
      const isAllDay = event.allDay;
      const backgroundColor = event.backgroundColor || '#1976d2';
      const borderColor = eventInfo.event.borderColor || backgroundColor;

      return <CalendarMonthEventCircle allDay={isAllDay} backgroundColor={backgroundColor} borderColor={borderColor} />;
    }

    // 주간/일간 뷰에서는 기존 텍스트 표시
    return <CalendarTimeGridEventContent title={event.title} />;
  }, []);

  // FullCalendar custom button 핸들러 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const handleRefetchEvents = () => {
    requestCalendarRefresh();
  };

  // 날짜별 제한 데이터 가져오기 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const fetchCalendarLimits = async (startDate, endDate) => {
    try {
      return await apiClient.get('/calendar-limits', {
        start_date: startDate,
        end_date: endDate,
      });
    } catch (error) {
      return null;
    }
  };

  // 타이틀 크기 자동 조정 함수 (디바운스 적용)
  const adjustTitleSize = useCallback(() => {
    if (adjustTitleSizeTimeoutRef.current) {
      clearTimeout(adjustTitleSizeTimeoutRef.current);
    }

    calendarPerf.count('dom.titleResize.scheduled', 1, { lang });
    adjustTitleSizeTimeoutRef.current = setTimeout(() => {
      calendarPerf.measure('dom.titleResize.ms', () => {
        const titleElement = document.querySelector('.calendar-title-text');
        if (!titleElement) {
          calendarPerf.count('dom.titleResize.skipped.noTitle');
          return;
        }

        titleElement.style.fontSize = '1.7em';
        titleElement.style.animation = 'none';
        titleElement.style.whiteSpace = 'normal';
        titleElement.style.overflow = 'visible';

        const container = titleElement.parentElement;
        if (!container) {
          calendarPerf.count('dom.titleResize.skipped.noContainer');
          return;
        }

        const containerWidth = container.offsetWidth - 120;
        if (containerWidth <= 0) {
          calendarPerf.count('dom.titleResize.skipped.nonPositiveWidth');
          return;
        }

        void titleElement.offsetHeight;

        const minFontSize = 1.2;
        const adjustedMinFontSize = lang === 'ko' ? 0.9 : minFontSize;

        let currentFontSize = 1.7;
        const maxIterations = 20;
        let iterations = 0;

        while (iterations < maxIterations) {
          titleElement.style.setProperty('font-size', `${currentFontSize}em`, 'important');
          void titleElement.offsetHeight;

          const currentWidth = titleElement.scrollWidth;
          if (currentWidth <= containerWidth) {
            break;
          }

          currentFontSize -= 0.05;

          if (currentFontSize < adjustedMinFontSize) {
            titleElement.style.setProperty('font-size', `${adjustedMinFontSize}em`, 'important');
            break;
          }

          iterations++;
        }

        calendarPerf.sample('dom.titleResize.iterations', iterations, { lang });
      }, { lang });
    }, 150);
  }, [calendarPerf, lang]);

  // FullCalendar 뷰/날짜 변경 시 타이틀 갱신
  const handleDatesSet = useCallback((arg) => {
    calendarPerf.count('navigation.datesSet', 1, { viewType: arg.view.type });
    setCurrentViewType(arg.view.type);
    setCalendarTitle(arg.view.title);

    // 날짜 변경 후 타이틀 크기 자동 조정 (디바운스 적용)
    adjustTitleSize();
    scheduleStickyHeaderSync(120);
    scheduleTimeGridStructureAdjustments(180);

    // 날짜 변경 시 현재 시간 선 다시 추가 (터치 반응성 개선을 위해 지연 시간 단축)
    if (isTimeGridView(arg.view.type)) {
      scheduleCurrentTimeLineUpdate(150);
      scheduleTimeGridDecorations(220);
    }
  }, [adjustTitleSize, calendarPerf, scheduleCurrentTimeLineUpdate, scheduleStickyHeaderSync, scheduleTimeGridDecorations, scheduleTimeGridStructureAdjustments]);

  // 터치 스와이프 이동 기능 제거됨

  // 컴포넌트 마운트 시와 윈도우 크기 변경 시 타이틀 크기 조정 및 직선 길이 계산
  // 윈도우 크기 변경 시 타이틀 크기 조정
  useEffect(() => {
    // 초기 로드 시 타이틀 크기 조정
    adjustTitleSize();

    window.addEventListener('resize', adjustTitleSize);

    return () => {
      window.removeEventListener('resize', adjustTitleSize);
      // 컴포넌트 언마운트 시 타이머 정리
      if (adjustTitleSizeTimeoutRef.current) {
        clearTimeout(adjustTitleSizeTimeoutRef.current);
      }
    };
  }, [adjustTitleSize]);





  // 뷰 변경 핸들러
  const handleChangeView = useCallback((viewType) => {
    calendarPerf.count('navigation.changeView', 1, { viewType });
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView(viewType);
      setCurrentViewType(viewType);

      // 뷰별 설정 동적 적용
      setTimeout(() => {
        calendarPerf.measure('navigation.changeView.applyMs', () => {
          if (viewType === 'dayGridMonth') {
            // 월간 뷰: 요일만 표시
            api.setOption('dayHeaderFormat', { weekday: 'short' });
          } else if (viewType === 'timeGridDay') {
            // 일간 뷰: 요일 + 일 + 월 표시, 1시간 간격
            api.setOption('dayHeaderFormat', { weekday: 'short', day: 'numeric', month: 'short' });
            api.setOption('slotDuration', CALENDAR_DISPLAY.slotDuration);
            api.setOption('slotLabelInterval', CALENDAR_DISPLAY.slotDuration);
            api.setOption('slotMinTime', isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00');
            api.setOption('scrollTime', isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00');
          } else if (viewType === 'timeGridWeek') {
            // 주간 뷰: 요일 + 일 표시, 1시간 간격
            api.setOption('dayHeaderFormat', { weekday: 'short', day: 'numeric' });
            api.setOption('slotDuration', CALENDAR_DISPLAY.slotDuration);
            api.setOption('slotLabelInterval', CALENDAR_DISPLAY.slotDuration);
            api.setOption('slotMinTime', isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00');
            api.setOption('scrollTime', isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00');
          }
          adjustTitleSize();
          scheduleStickyHeaderSync(140);
          scheduleTimeGridStructureAdjustments(180);

          // 현재 시간 선 업데이트 (터치 반응성 개선을 위해 지연 시간 단축)
          if (isTimeGridView(viewType)) {
            scheduleCurrentTimeLineUpdate(100);
            scheduleTimeGridDecorations(260);
          }
        }, { viewType });
      }, 100);
    }
  }, [adjustTitleSize, calendarPerf, isEarlyHoursCollapsed, scheduleCurrentTimeLineUpdate, scheduleStickyHeaderSync, scheduleTimeGridDecorations, scheduleTimeGridStructureAdjustments]);





  // 이동 핸들러
  const handlePrev = useCallback(() => {
    calendarPerf.count('navigation.prev');
    const api = calendarRef.current?.getApi();
    if (api) {
      api.prev();
      scheduleStickyHeaderSync(100);
      scheduleTimeGridStructureAdjustments(140);
    }
  }, [calendarPerf, scheduleStickyHeaderSync, scheduleTimeGridStructureAdjustments]);
  const handleNext = useCallback(() => {
    calendarPerf.count('navigation.next');
    const api = calendarRef.current?.getApi();
    if (api) {
      api.next();
      scheduleStickyHeaderSync(100);
      scheduleTimeGridStructureAdjustments(140);
    }
  }, [calendarPerf, scheduleStickyHeaderSync, scheduleTimeGridStructureAdjustments]);
  const handleToday = useCallback(() => {
    calendarPerf.count('navigation.today');
    const api = calendarRef.current?.getApi();
    if (api) {
      api.today();
      scheduleStickyHeaderSync(100);
      scheduleTimeGridStructureAdjustments(140);
    }

    // 반짝이는 효과 시작 (3번 반복, 총 1초)
    setShiningToday(true);
    setTimeout(() => setShiningToday(false), 1000);
  }, [calendarPerf, scheduleStickyHeaderSync, scheduleTimeGridStructureAdjustments]);

  // URL 파라미터 처리 및 초기 설정
  useEffect(() => {
    // 이미 처리된 경우 스킵
    if (urlProcessed) return;

    // URL 파라미터에서 view, date, highlightEvent 정보 추출
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const dateParam = searchParams.get('date');
    if (isDev()) console.log('URL 파라미터:', { viewParam, dateParam });

    // 뷰 타입 설정
    if (viewParam) {
      setCurrentViewType(viewParam);
    }

    // 날짜 설정
    if (dateParam) {
      try {
        const targetDate = new Date(dateParam);
        if (!isNaN(targetDate.getTime())) {
          setInitialDate(targetDate);
          if (isDev()) console.log('설정된 날짜:', targetDate);

          // FullCalendar가 이미 마운트된 경우 즉시 날짜 변경
          if (calendarRef.current) {
            setTimeout(() => {
              try {
                const calendarApi = calendarRef.current.getApi();
                if (calendarApi) {
                  if (isDev()) console.log('즉시 날짜 설정:', targetDate);
                  calendarApi.gotoDate(targetDate);
                }
              } catch (error) {
                console.error('즉시 날짜 설정 에러:', error);
              }
            }, 100);
          }
        }
      } catch (error) {
        console.error('날짜 설정 에러:', error);
      }
    }



    // 처리 완료 플래그 설정
    setUrlProcessed(true);
  }, [location.search, urlProcessed]);

  // 초기 로드 시 월간 뷰 설정
  useEffect(() => {
    const api = getCalendarApi();
    if (api && currentViewType === 'dayGridMonth') {
      api.setOption('dayHeaderFormat', { weekday: 'short' });
    }
  }, [currentViewType, getCalendarApi]);

  useEffect(() => {
    if (!isTimeGridView(currentViewType)) {
      clearScheduledTimeGridStructureAdjustments();
      return undefined;
    }

    scheduleTimeGridStructureAdjustments(220);

    const handleResize = () => {
      scheduleTimeGridStructureAdjustments(140);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearScheduledTimeGridStructureAdjustments();
    };
  }, [clearScheduledTimeGridStructureAdjustments, currentViewType, scheduleTimeGridStructureAdjustments]);



  // 30분 지점 점선과 10시 시간대 핑크색 가로선 추가를 위한 useEffect
  useEffect(() => {
    if (!isTimeGridView(currentViewType)) {
      clearScheduledTimeGridDecorations();
      return undefined;
    }

    scheduleTimeGridDecorations(300);

    const handleResize = () => {
      scheduleTimeGridDecorations(180);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearScheduledTimeGridDecorations();
    };
  }, [clearScheduledTimeGridDecorations, currentViewType, scheduleTimeGridDecorations]);

  const calendarModeOptions = [
    { value: 'normal', label: TEXT.normalMode[lang] },
    { value: 'add', label: TEXT.addMode[lang] },
    { value: 'offday', label: TEXT.offdayMode[lang] },
    ...(hasCalendarAdminModeAccess ? [
      { value: 'admin', label: TEXT.adminMode[lang] },
      { value: 'bidon', label: TEXT.bidonMode[lang] }
    ] : [])
  ];


  return (

    <div className={`calendar-wrapper ${calendarMode === 'add' ? 'add-mode' : ''} ${calendarMode === 'offday' ? 'offday-mode' : ''}`} ref={calendarWrapperRef}>
      {/* 커스텀 캘린더 헤더 */}
      <div className="calendar-custom-header">
        <div className="calendar-title-row">
          <button
            className="calendar-nav-circle"
            onClick={handlePrev}
            style={{
              width: '40px',
              height: '40px',
              border: '1px solid #000',
              backgroundColor: '#fffdf8',
              cursor: 'pointer',
              borderRadius: '50%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              zIndex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              outline: 'none'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M8 2L4 6L8 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="calendar-title-text" style={{ fontWeight: 300 }}>{calendarTitle}</span>
          <button
            className="calendar-nav-circle"
            onClick={handleNext}
            style={{
              width: '40px',
              height: '40px',
              border: '1px solid #000',
              backgroundColor: '#fffdf8',
              cursor: 'pointer',
              borderRadius: '50%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              zIndex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              outline: 'none'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        {/* 탭 네비게이션 */}
        <div style={{
          display: 'flex',
          marginBottom: '5px',
          gap: '0',
          justifyContent: 'center',
          position: 'relative'
        }}>
          {/* 왼쪽 직선 */}


          {/* 오른쪽 직선 */}


          {/* 탭 컨테이너 */}
          <div
            className="calendar-tab-container"
            style={{
              display: 'flex',
              gap: '0',
              justifyContent: 'center',
              border: '1px solid #000',
              borderRadius: '8px',
              padding: '0'
            }}
          >

            <button
              className="calendar-tab-btn"
              onClick={handleToday}
              style={{
                padding: '4px 16px',
                border: '1px solid transparent',
                backgroundColor: '#fffdf8',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRight: '1px solid transparent',
                transform: 'scale(1)',
                zIndex: '1',
                animation: shiningToday ? 'tabShine 0.33s ease-in-out 3' : 'none'
              }}
            >
              {TEXT.today[lang]}
            </button>
            <button
              className="calendar-tab-btn"
              onClick={() => handleChangeView('dayGridMonth')}
              style={{
                padding: '4px 16px',
                border: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                backgroundColor: currentViewType === 'dayGridMonth' ? '#fffdf8' : '#fffdf8',
                color: currentViewType === 'dayGridMonth' ? '#000' : '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                borderRight: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                transform: currentViewType === 'dayGridMonth' ? 'scale(1.15)' : 'scale(1)',
                zIndex: currentViewType === 'dayGridMonth' ? '2' : '1'
              }}
            >
              {TEXT.month[lang]}
            </button>
            <button
              className="calendar-tab-btn"
              onClick={() => handleChangeView('timeGridWeek')}
              style={{
                padding: '4px 16px',
                border: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                backgroundColor: currentViewType === 'timeGridWeek' ? '#fffdf8' : '#fffdf8',
                color: currentViewType === 'timeGridWeek' ? '#000' : '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                borderRight: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                transform: currentViewType === 'timeGridWeek' ? 'scale(1.15)' : 'scale(1)',
                zIndex: currentViewType === 'timeGridWeek' ? '2' : '1'
              }}
            >
              {TEXT.week[lang]}
            </button>

            <button
              className="calendar-tab-btn"
              onClick={() => handleChangeView('timeGridDay')}
              style={{
                padding: '4px 16px',
                border: currentViewType === 'timeGridDay' ? '1px solid #000' : '1px solid transparent',
                backgroundColor: currentViewType === 'timeGridDay' ? '#fffdf8' : '#fffdf8',
                color: currentViewType === 'timeGridDay' ? '#000' : '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: currentViewType === 'timeGridDay' ? '1px solid #000' : '1px solid transparent',
                borderRight: currentViewType === 'timeGridDay' ? '1px solid #000' : '1px solid transparent',
                transform: currentViewType === 'timeGridDay' ? 'scale(1.15)' : 'scale(1)',
                zIndex: currentViewType === 'timeGridDay' ? '2' : '1'
              }}
            >
              {TEXT.day[lang]}
            </button>

            {/* 0-8시 시간대 토글 버튼 */}
            <button
              className="calendar-tab-btn"
              onClick={currentViewType === 'dayGridMonth' ? undefined : toggleEarlyHours}
              style={{
                padding: '4px 16px',
                border: '1px solid transparent',
                backgroundColor: '#fffdf8',
                color: currentViewType === 'dayGridMonth' ? '#ccc' : '#000',
                cursor: currentViewType === 'dayGridMonth' ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'scale(1)',
                zIndex: '1',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: currentViewType === 'dayGridMonth' ? '0.6' : '1'
              }}
              title={currentViewType === 'dayGridMonth' ? '월간 뷰에서는 사용할 수 없습니다' : (isEarlyHoursCollapsed ? '0-8시 시간대 펼치기' : '0-8시 시간대 접기')}
            >
              <span>0-8h</span>
              <span style={{ fontSize: '12px' }}>
                {isEarlyHoursCollapsed ? '▼' : '▲'}
              </span>
            </button>

          </div>
        </div>
      </div>
      {/* 모드선택패널 */}
      <div className="calendar-mode-selector">
        {calendarModeOptions.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={`calendar-radio calendar-mode-button ${calendarMode === value ? 'calendar-mode-button-active' : ''}`}
            aria-pressed={calendarMode === value}
            onClick={() => setCalendarMode(value)}
          >
            {label}
          </button>
        ))}

        {/* 도움말 아이콘 */}
        <div className="help-icon-container">
          <button ref={helpIconButtonRef} className="help-icon-btn" onClick={toggleHelpModal} title={TEXT.helpTitle[lang]}>
            ?
          </button>
          {helpModalOpen && typeof document !== 'undefined' && createPortal(
            <div className="help-modal" style={helpModalPosition}>
              <div className="help-modal-content">
                <div className="help-modal-header">
                  <h3>{TEXT.helpTitle[lang]}</h3>
                  <button className="help-modal-close" onClick={() => setHelpModalOpen(false)}>×</button>
                </div>
                <div className="help-modal-body">
                  <p><strong>{TEXT.normalMode[lang]}</strong>: {TEXT.helpNormal[lang].split(':')[1]}</p>
                  <p><strong>{TEXT.addMode[lang]}</strong>: {TEXT.helpAdd[lang].split(':')[1]}</p>
                  <p><strong>{TEXT.offdayMode[lang]}</strong>: {TEXT.helpOff[lang].split(':')[1]}</p>
                  {hasCalendarAdminModeAccess && (
                    <p><strong>{TEXT.adminMode[lang]}</strong>: {TEXT.helpAdmin[lang].split(':').slice(1).join(':')}</p>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
        {safetyEvents.length > 0 && (
          <CalendarReportMenu events={safetyEvents} lang={lang} onOpen={() => setHelpModalOpen(false)} />
        )}
      </div>
      {/* Safari 호환성을 위한 'Sandwich' 테두리 방식: 배경색으로 테두리 시뮬레이션 */}
      <div className="calendar-border-outer" style={{
        '--calendar-axis-background': CALENDAR_DISPLAY.axisColor,
        '--calendar-opening-color': CALENDAR_DISPLAY.studioOpeningColor,
        '--calendar-bidon-background': CALENDAR_DISPLAY.bidonStyle.background,
        '--calendar-bidon-border': CALENDAR_DISPLAY.bidonStyle.border,
        '--calendar-bidon-leading-border': CALENDAR_DISPLAY.bidonStyle.leadingBorder,
        '--calendar-bidon-title': CALENDAR_DISPLAY.bidonStyle.title,
        '--calendar-bidon-time': CALENDAR_DISPLAY.bidonStyle.time,
        backgroundColor: '#000',
        padding: '1px',
        borderRadius: '10px',
        marginTop: '10px',
        overflow: 'hidden'
      }}>
        <div className="calendar-border-inner" style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative' // z-index 컨텍스트 생성
        }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
            timeZone="Europe/Paris"
            initialView={currentViewType}
            initialDate={initialDate}
            headerToolbar={false}
            locales={[customFrLocale, customKoLocale, customEnLocale, customLocale]}
            locale={getCalendarLocale(lang)}
            events={fetchEvents}
            dateClick={handleDateClick}
            views={{
              timeGridWeek: {
                allDaySlot: true,
                eventDisplay: 'block',
                dayMaxEvents: isMobile ? 6 : true,
                slotDuration: CALENDAR_DISPLAY.slotDuration, // 시각적으로 1시간 간격
                slotLabelInterval: CALENDAR_DISPLAY.slotDuration, // 라벨도 1시간 간격
                slotMinTime: isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00',
                scrollTime: isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00'
              },
              timeGridDay: {
                allDaySlot: true,
                eventDisplay: 'block',
                dayMaxEvents: isMobile ? 6 : true,
                slotDuration: CALENDAR_DISPLAY.slotDuration, // 시각적으로 1시간 간격
                slotLabelInterval: CALENDAR_DISPLAY.slotDuration, // 라벨도 1시간 간격
                slotMinTime: isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00',
                scrollTime: isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00'
              },
              dayGridMonth: {
                allDaySlot: true,
                eventDisplay: 'block',
                dayMaxEvents: isMobile ? 6 : true
              }
            }}

            select={isCalendarEventCreationMode(calendarMode) || calendarMode === 'admin' ? handleDateSelect : undefined}
            eventClick={handleEventClick}
            editable={true}
            selectable={isCalendarEventCreationMode(calendarMode) || calendarMode === 'admin'}
            selectMirror={true}
            eventStartEditable={true}
            eventDurationEditable={true}
            eventAllow={(_, draggedEvent) => canEditEvent(draggedEvent)}
            height="auto"
            dayMaxEvents={isMobile ? 6 : true}
            slotDuration={CALENDAR_DISPLAY.slotDuration}
            slotMinTime={isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00'}
            scrollTime={isEarlyHoursCollapsed ? CALENDAR_DISPLAY.collapsedStartTime : '00:00:00'}
            slotLabelInterval={CALENDAR_DISPLAY.slotDuration}
            snapDuration={CALENDAR_DISPLAY.snapDuration}
            allDaySlot={true}
            eventDisplay="auto"
            displayEventTime={false}
            displayEventEnd={false}

            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            slotLabelFormat={{
              hour: '2-digit',
              hour12: false
            }}
            dayHeaderContent={renderDayHeaderContent}
            eventDrop={handleEventDrop}
            eventContent={renderEventContent}
            eventClassNames={eventClassNames}
            datesSet={handleDatesSet}
            // 터치 반응성 설정
            eventLongPressDelay={400}
            selectLongPressDelay={400}

          />
        </div>
      </div>
      {/* 하단 네비게이션 버튼 */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '15px',
        gap: '15px'
      }}>
        {/* 월/년 네비게이션 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '0 10px'
        }}>
          <button
            className="calendar-nav-circle"
            onClick={handlePrev}
            style={{
              width: '40px',
              height: '40px',
              border: '1px solid #000',
              backgroundColor: '#fffdf8',
              cursor: 'pointer',
              borderRadius: '50%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              zIndex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              outline: 'none'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M8 2L4 6L8 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span
            className="bottom-calendar-title"
            style={{
              fontSize: '1.36em',
              fontWeight: '300 !important',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: '#222',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1'
            }}
          >
            {calendarTitle}
          </span>
          <button
            className="calendar-nav-circle"
            onClick={handleNext}
            style={{
              width: '40px',
              height: '40px',
              border: '1px solid #000',
              backgroundColor: '#fffdf8',
              cursor: 'pointer',
              borderRadius: '50%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              zIndex: '1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              outline: 'none'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        {/* 오늘 버튼 */}
        <button
          className="calendar-tab-btn"
          onClick={handleToday}
          style={{
            padding: '8px 16px',
            border: '1px solid #000',
            backgroundColor: '#fffdf8',
            color: '#000',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '400',
            borderRadius: '8px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)',
            zIndex: '1',
            animation: shiningToday ? 'tabShine 0.33s ease-in-out 3' : 'none'
          }}
        >
          {TEXT.today[lang]}
        </button>
      </div>
      {/* 제한설정 버튼 */}
      {hasFullAdminAccess && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '20px',
          padding: '10px 20px',
          borderTop: '1px solid #eee'
        }}>
          <button
            className="calendar-tab-btn"
            onClick={() => setShowLimitSettings(true)}
            style={{
              padding: '4px 16px',
              border: '1px solid #000',
              backgroundColor: '#fffdf8',
              color: '#000',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '400',
              borderRadius: '8px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              zIndex: '1'
            }}
          >
            {t('limitSettings', lang)}
          </button>
        </div>
      )}

      {
        modalOpen && (
          <Suspense fallback={<CalendarModalFallback lang={lang} />}>
            <AddEventModal
              date={selectedStartDate}
              startTime={selectedStartTime}
              endDate={selectedEndDate}
              endTime={selectedEndTime}
              viewType={currentViewType}
              calendarMode={calendarMode}
              onClose={() => {
                try {
                  setModalOpen(false);
                  setSelectedStartDate(null);
                  setSelectedStartTime('09:00');
                  setSelectedEndDate(null);
                  setSelectedEndTime('10:00');
                } catch (error) {
                  // 모달 닫기 중 에러
                }
              }}
              onEventAdded={() => {
                try {
                  setModalOpen(false);
                  setSelectedStartDate(null);
                  setSelectedStartTime('09:00');
                  setSelectedEndDate(null);
                  setSelectedEndTime('10:00');
                  handleEventAdded();
                } catch (error) {
                  // 이벤트 추가 후 모달 닫기 중 에러
                }
              }}
            />
          </Suspense>
        )
      }
      {editModalOpen && (
        <Suspense fallback={<CalendarModalFallback lang={lang} />}>
          <EditEventModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            event={selectedEvent}
            onEventUpdated={handleEventUpdated}
            onEventDeleted={handleEventDeleted}
            toUTCISOStringFromLocalInput={toUTCISOStringFromLocalInput}
            mutationScope={eventMutationScope}
          />
        </Suspense>
      )}
      {restrictedPeriodModalOpen && selectedRestrictedPeriod && (
        <EventModal
          show={restrictedPeriodModalOpen}
          eventData={selectedRestrictedPeriod}
          mode="restricted"
          onSave={handleRestrictedPeriodSave}
          onDelete={handleRestrictedPeriodDelete}
          onClose={closeRestrictedPeriodModal}
          saving={restrictedPeriodSaving}
        />
      )}
      {
        showLimitSettings && (
          <LimitSettingsModal
            show={showLimitSettings}
            onClose={() => setShowLimitSettings(false)}
          />
        )
      }
    </div >
  );
}

// CSS: blocked-x만 남기고 blocked-event 스타일 제거
if (!document.getElementById('blocked-event-style')) {
  const style = document.createElement('style');
  style.id = 'blocked-event-style';
  style.innerHTML = `
      .blocked-x {
        position: absolute;
      top: 6px;
      left: 8px;
      color: #fff;
      font-size: 1.2em;
      font-weight: bold;
      opacity: 0.95;
      pointer-events: none;
      user-select: none;
      z-index: 3;
    }

      .calendar-title-text {
        display: inline-block;
      transition: font-size 0.3s ease;
    }

      /* 현재 시간 가로선 애니메이션 */
      @keyframes currentTimeBlink {
        0 %, 100 % {
          opacity: 0.9;
          box- shadow: 0 0 10px rgba(255, 0, 0, 0.5);
      transform: scale(1);
      }
      50% {
        opacity: 1;
      box-shadow: 0 0 15px rgba(255, 0, 0, 0.8);
      transform: scale(1.05);
      }
    }





      .current-time-line {
        position: absolute !important;
      height: 1px !important;
      background: linear-gradient(90deg, #ff0000 0%, #ff6666 50%, #ff0000 100%) !important;
      border-radius: 1px !important;
      z-index: 9999999 !important;
      pointer-events: none !important;
      animation: currentTimeBlink 2s ease-in-out infinite !important;
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.5) !important;
    }

      .current-time-circle {
        position: absolute !important;
      width: 5px !important;
      height: 5px !important;
      background: #ff0000 !important;
      border-radius: 50% !important;
      border: 1px solid #fff !important;
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.7) !important;
      z-index: 10000000 !important;
      animation: currentTimeBlink 2s ease-in-out infinite !important;
    }
      `;
  document.head.appendChild(style);
}
